import { Link } from 'react-router-dom';
import { Alert } from '../../types';
import StatusBadge from '../common/StatusBadge';
import { BadgeCheck, ArrowRight } from 'lucide-react';

interface AlertsPanelProps {
  alerts: Alert[];
}

export default function AlertsPanel({ alerts }: AlertsPanelProps) {
  if (alerts.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card shadow-sm">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">
              Recent Alerts
            </h3>
            <Link
              to="/alerts"
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center"
            >
              Manage
              <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
              <BadgeCheck className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h4 className="text-sm font-medium text-foreground mb-1">
              All Clear!
            </h4>
            <p className="text-sm text-muted-foreground">
              No active alerts at this time.
            </p>
            <Link
              to="/alerts"
              className="inline-flex items-center mt-4 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Generate alerts to check for issues
              <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Count by severity
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-medium text-foreground">
              Recent Alerts
            </h3>
            {criticalCount > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-700 dark:text-red-400">
                {criticalCount} critical
              </span>
            )}
            {warningCount > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-700 dark:text-amber-400">
                {warningCount} warning
              </span>
            )}
          </div>
          <Link
            to="/alerts"
            className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center"
          >
            View all
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        <div className="space-y-3">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={`flex items-start space-x-4 p-4 rounded-lg border-l-4 ${
                alert.severity === 'critical'
                  ? 'bg-red-500/5 dark:bg-red-500/10 border-l-red-500'
                  : alert.severity === 'warning'
                  ? 'bg-amber-500/5 dark:bg-amber-500/10 border-l-amber-500'
                  : 'bg-blue-500/5 dark:bg-blue-500/10 border-l-blue-500'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <StatusBadge status={alert.severity} type="severity" />
                  <span className="text-xs text-muted-foreground">
                    {new Date(alert.createdDate).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm font-medium text-foreground truncate">
                  {alert.title}
                </p>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {alert.message}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
